import {
  ApiApi,
  ApiCustomFieldValuesCreateRequest,
  ApiCustomFieldValuesPartialUpdateRequest,
  CustomFieldDefinition,
  CustomFieldValueRead,
  FieldTypeEnum,
} from '@api';
import { withQueryParams } from '@helpers';
import { NewFile } from '@types';
import { Dispatch, SetStateAction, useCallback } from 'react';

type SaveCustomFieldTargetModel = 'aiModel' | 'useCase';

type SaveCustomFieldValuesParams = {
  definitions?: ReadonlyArray<CustomFieldDefinition>;
  values?: Record<number, string | NewFile>;
  existingValues?: ReadonlyArray<CustomFieldValueRead>;
  targetModel: SaveCustomFieldTargetModel;
  targetId: number;
};

const isNewFile = (value?: string | NewFile): value is NewFile =>
  !!value && typeof value !== 'string' && value.file instanceof File;

const toTargetModelQueryParam = (targetModel: SaveCustomFieldTargetModel) =>
  targetModel === 'aiModel' ? 'ai_model' : 'use_case';

const hasTextCustomFieldValue = (rawValue: string, fieldType: FieldTypeEnum) => {
  if (fieldType === FieldTypeEnum.file) {
    return rawValue.trim().length > 0;
  }

  return rawValue.trim().length > 0;
};

export const createCustomFieldValueChangeHandler =
  (
    setCustomFieldValues: Dispatch<SetStateAction<Record<number, string | NewFile>>>,
    setCustomFieldErrors: Dispatch<SetStateAction<Record<number, string>>>,
  ) =>
  (fieldId: number, value: string | NewFile) => {
    setCustomFieldValues(previousValues => ({ ...previousValues, [fieldId]: value }));
    setCustomFieldErrors(previousErrors => {
      if (!previousErrors[fieldId]) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors[fieldId];

      return nextErrors;
    });
  };

export const validateCustomFieldValues = (
  definitions: ReadonlyArray<CustomFieldDefinition> = [],
  values: Record<number, string | NewFile> = {},
): Record<number, string> =>
  definitions.reduce<Record<number, string>>((errors, definition) => {
    const rawValue = values[definition.id];
    const hasValue =
      typeof rawValue === 'string' ? hasTextCustomFieldValue(rawValue, definition.fieldType) : isNewFile(rawValue);

    if (!definition.required || hasValue) {
      return errors;
    }

    return {
      ...errors,
      [definition.id]:
        definition.fieldType === FieldTypeEnum.file ? 'Please upload a file' : `Please enter ${definition.name}`,
    };
  }, {});

export const mapExistingCustomFieldValues = (
  definitions: ReadonlyArray<CustomFieldDefinition> = [],
  existingValues: ReadonlyArray<CustomFieldValueRead> = [],
): Record<number, string> => {
  const existingValuesByDefinitionId = new Map(existingValues.map(value => [value.definition.id, value.value]));

  return definitions.reduce<Record<number, string>>((result, definition) => {
    const value = existingValuesByDefinitionId.get(definition.id);

    if (value === undefined) {
      return result;
    }

    return {
      ...result,
      [definition.id]: value,
    };
  }, {});
};

export const useCustomFieldValues = (api: ApiApi) => {
  const saveCustomFieldValues = useCallback(
    async ({
      definitions = [],
      values = {},
      existingValues = [],
      targetModel,
      targetId,
    }: SaveCustomFieldValuesParams): Promise<boolean> => {
      const existingValuesByDefinitionId = new Map(existingValues.map(value => [value.definition.id, value]));

      const buildRequest = (definition: CustomFieldDefinition): Promise<unknown> | null => {
        const rawValue = values[definition.id];
        const existingValue = existingValuesByDefinitionId.get(definition.id);

        const trimmedTextValue = typeof rawValue === 'string' ? rawValue.trim() : undefined;
        const hasFileValue = isNewFile(rawValue);
        const hasTextValue =
          typeof rawValue === 'string' ? hasTextCustomFieldValue(rawValue, definition.fieldType) : false;
        const hasValue = hasTextValue || hasFileValue;
        const apiWithTargetModel = withQueryParams(api, {
          target_model: toTargetModelQueryParam(targetModel),
        });

        if (!hasValue) {
          return existingValue
            ? apiWithTargetModel.apiCustomFieldValuesDestroy({ id: String(existingValue.customFieldValueId) })
            : null;
        }

        const rawStringValue = typeof rawValue === 'string' ? rawValue : undefined;
        const shouldSkipFileValue =
          typeof rawStringValue === 'string' &&
          !!existingValue &&
          existingValue.definition.fieldType === FieldTypeEnum.file &&
          rawStringValue.trim() === existingValue.value;
        const shouldSkipTextValue =
          typeof rawStringValue === 'string' &&
          !!existingValue &&
          existingValue.definition.fieldType !== FieldTypeEnum.file &&
          rawStringValue.trim() === existingValue.value;
        const shouldSkip = shouldSkipFileValue || shouldSkipTextValue;

        if (shouldSkip) {
          return null;
        }

        const isFileField = definition.fieldType === FieldTypeEnum.file;
        const textValue = isFileField ? undefined : trimmedTextValue;
        const fileValue = isFileField && hasFileValue ? rawValue.file : undefined;

        if (existingValue) {
          const updateParams: ApiCustomFieldValuesPartialUpdateRequest = {
            id: String(existingValue.customFieldValueId),
            textValue,
            fileValue,
          };

          return apiWithTargetModel.apiCustomFieldValuesPartialUpdate(updateParams);
        }

        const createParams: ApiCustomFieldValuesCreateRequest = {
          field: definition.id,
          ...(targetModel === 'aiModel' ? { aiModel: targetId } : { useCase: targetId }),
          textValue,
          fileValue,
        };

        return apiWithTargetModel.apiCustomFieldValuesCreate(createParams);
      };

      const requests = definitions
        .map(definition => {
          const request = buildRequest(definition);

          if (!request) {
            return null;
          }

          return { definition, request };
        })
        .filter(
          (
            request,
          ): request is {
            definition: CustomFieldDefinition;
            request: Promise<unknown>;
          } => request !== null,
        );

      if (requests.length === 0) {
        return true;
      }

      const results = await Promise.allSettled(requests.map(({ request }) => request));

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          return;
        }

        const { definition } = requests[index];

        console.error('Failed to save custom field value', {
          fieldId: definition.id,
          fieldName: definition.name,
          fieldType: definition.fieldType,
          reason: result.reason,
        });
      });

      return results.every(result => result.status === 'fulfilled');
    },
    [api],
  );

  return { saveCustomFieldValues, validateCustomFieldValues, mapExistingCustomFieldValues };
};
